import {
  type FormalWorkspaceSessionScope,
  formalWorkspaceSessionStorageKey,
  legacyFormalWorkspaceSessionStorageKey,
  parseFormalWorkspaceSnapshot,
  serializeFormalWorkspaceTabs,
} from './formalWorkspaceSession';
import { isFormalNewQuoteId } from './quoteLegacyProjection';

const DATABASE = 'quotation-system-workspace-recovery';
const STORE = 'snapshots';

export interface FormalWorkspaceRecoveryPort {
  load(scope: FormalWorkspaceSessionScope): Promise<string | null>;
  save(
    scope: FormalWorkspaceSessionScope,
    serialized: string,
    mergePrevious?: (previous: string | null) => string,
  ): Promise<string>;
  takeOver?(
    scope: FormalWorkspaceSessionScope,
    serialized: string,
    conflictingSnapshot: string | null,
    mergePrevious?: (previous: string | null) => string,
  ): Promise<string>;
}

export class WorkspaceRecoveryConflictError extends Error {
  constructor(readonly conflictingSnapshot: string | null) {
    super('另一页面已更新工作台恢复记录，已保留本页内容，请先核对另一页面。');
    this.name = 'WorkspaceRecoveryConflictError';
  }
}

type Options = {
  legacyStorage: Pick<Storage, 'getItem'>;
  indexedDB?: IDBFactory | null;
};

function validSnapshot(
  value: unknown,
  scope: FormalWorkspaceSessionScope,
): string {
  if (typeof value !== 'string' || !parseFormalWorkspaceSnapshot(value, scope))
    throw new Error('工作台恢复数据与当前身份或权限不匹配，请保留现场后重试。');
  return value;
}

/** Saved server quotes have no tab-local business edits to protect. */
function hasUnsavedWork(
  raw: string | null,
  nextRaw: string,
  scope: FormalWorkspaceSessionScope,
): boolean {
  if (!raw) return false;
  const tabs = parseFormalWorkspaceSnapshot(raw, scope);
  const nextTabs = parseFormalWorkspaceSnapshot(nextRaw, scope);
  if (!tabs || !nextTabs) return true;
  return Object.entries(tabs.quotes).some(([id, quote]) => {
    const hidden = !tabs.openQuoteIds.includes(id);
    // Case rehearsals are local-only; an automatic write is safe only when
    // the merged snapshot preserves the entire hidden quote unchanged.
    if (
      hidden &&
      quote.caseRehearsalSnapshot &&
      JSON.stringify(nextTabs.quotes[id]) === JSON.stringify(quote)
    )
      return false;
    return quote.dirty || isFormalNewQuoteId(id) || hidden;
  });
}

function openDatabase(
  factory: IDBFactory | null | undefined,
): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    if (!factory) {
      reject(new Error('浏览器工作台恢复存储不可用，请恢复浏览器存储后重试。'));
      return;
    }
    let settled = false;
    let request: IDBOpenDBRequest;
    try {
      request = factory.open(DATABASE, 1);
    } catch (cause) {
      reject(new Error('无法打开工作台恢复存储，请稍后重试。', { cause }));
      return;
    }
    request.onupgradeneeded = () => {
      if (!request.result.objectStoreNames.contains(STORE))
        request.result.createObjectStore(STORE);
    };
    request.onblocked = () => {
      settled = true;
      reject(
        new Error('工作台恢复存储升级被其他页面占用，请关闭旧页面后重试。'),
      );
    };
    request.onerror = () => {
      settled = true;
      reject(
        new Error('无法打开工作台恢复存储，请稍后重试。', {
          cause: request.error,
        }),
      );
    };
    request.onsuccess = () => {
      const database = request.result;
      database.onversionchange = () => database.close();
      if (settled) database.close();
      else {
        settled = true;
        resolve(database);
      }
    };
  });
}

/** Resolves only after the IDB transaction commits, never on request success. */
function transaction<T>(
  database: IDBDatabase,
  mode: IDBTransactionMode,
  work: (
    store: IDBObjectStore,
    result: (value: T) => void,
    fail: (error: unknown) => void,
  ) => void,
): Promise<T> {
  return new Promise((resolve, reject) => {
    let tx: IDBTransaction;
    try {
      tx = database.transaction(STORE, mode);
    } catch (cause) {
      reject(new Error('无法访问工作台恢复存储，请稍后重试。', { cause }));
      return;
    }
    let value: T;
    let hasResult = false;
    let failure: unknown;
    let failed = false;
    const abort = (error: unknown) => {
      if (!failed) {
        failed = true;
        failure = error;
      }
      try {
        tx.abort();
      } catch {
        reject(failure);
      }
    };
    tx.onabort = () =>
      reject(
        failed
          ? failure
          : new Error('工作台恢复存储事务已取消，请重试。', {
              cause: tx.error,
            }),
      );
    tx.onerror = (event) =>
      abort(
        new Error('工作台恢复存储读写失败，请恢复浏览器存储后重试。', {
          cause: (event.target as IDBRequest | null)?.error ?? tx.error,
        }),
      );
    database.onerror = (event) =>
      abort(
        new Error('工作台恢复存储发生错误，请稍后重试。', {
          cause: (event.target as IDBRequest | null)?.error ?? tx.error,
        }),
      );
    tx.oncomplete = () => {
      if (failed) reject(failure);
      else if (!hasResult)
        reject(new Error('工作台恢复存储未返回结果，请重试。'));
      else resolve(value);
    };
    try {
      work(
        tx.objectStore(STORE),
        (next) => {
          value = next;
          hasResult = true;
        },
        abort,
      );
    } catch (error) {
      abort(error);
    }
  });
}

type StoredRecord = { exists: boolean; value: unknown };

/** An existing undefined value is corrupt, not an absent key eligible for migration. */
function readRecord(
  store: IDBObjectStore,
  key: string,
  result: (record: StoredRecord) => void,
): void {
  const presence = store.getKey(key),
    value = store.get(key);
  let presenceReady = false,
    valueReady = false;
  const finish = () => {
    if (presenceReady && valueReady)
      result({ exists: presence.result !== undefined, value: value.result });
  };
  presence.onsuccess = () => {
    presenceReady = true;
    finish();
  };
  value.onsuccess = () => {
    valueReady = true;
    finish();
  };
}

export function createFormalWorkspaceRecoveryStore(
  options: Options,
): FormalWorkspaceRecoveryPort {
  const queues = new Map<string, Promise<unknown>>();
  const expected = new Map<string, string | null>();
  const queued = <T>(key: string, work: () => Promise<T>): Promise<T> => {
    const previous = queues.get(key) ?? Promise.resolve();
    const result = previous.catch(() => undefined).then(work);
    const tail = result.then(
      () => undefined,
      () => undefined,
    );
    queues.set(key, tail);
    void tail.then(() => {
      if (queues.get(key) === tail) queues.delete(key);
    });
    return result;
  };
  const withDatabase = async <T>(work: (db: IDBDatabase) => Promise<T>) => {
    const factory = Object.hasOwn(options, 'indexedDB')
      ? options.indexedDB
      : globalThis.indexedDB;
    const database = await openDatabase(factory);
    try {
      return await work(database);
    } finally {
      database.close();
    }
  };
  const write = (
    database: IDBDatabase,
    key: string,
    scope: FormalWorkspaceSessionScope,
    candidate: string,
    mergePrevious?: (previous: string | null) => string,
    onlyIfMissing = false,
    expectedPrevious?: string | null,
    takeoverPrevious?: string | null,
  ) =>
    transaction<string>(database, 'readwrite', (store, result, fail) => {
      readRecord(store, key, (read) => {
        try {
          const previous = read.exists
            ? validSnapshot(read.value, scope)
            : null;
          const next =
            onlyIfMissing && previous !== null
              ? previous
              : validSnapshot(
                  mergePrevious ? mergePrevious(previous) : candidate,
                  scope,
                );
          if (!onlyIfMissing && takeoverPrevious !== undefined) {
            if (previous !== takeoverPrevious)
              throw new WorkspaceRecoveryConflictError(previous);
            if (previous !== null)
              store.put(
                previous,
                `${key}:replaced:${globalThis.crypto.randomUUID()}`,
              );
          } else if (
            !onlyIfMissing &&
            next !== previous &&
            previous !== expectedPrevious &&
            hasUnsavedWork(previous, next, scope)
          )
            throw new WorkspaceRecoveryConflictError(previous);
          if (!(onlyIfMissing && previous !== null) && next !== previous)
            store.put(next, key);
          result(next);
        } catch (error) {
          // Native transactions must not commit after validation/merge errors.
          fail(error);
        }
      });
    });
  return {
    load(scope) {
      const key = formalWorkspaceSessionStorageKey(scope);
      return queued(key, async () => {
        const loaded = await withDatabase(async (database) => {
          const current = await transaction<StoredRecord>(
            database,
            'readonly',
            (store, result) => readRecord(store, key, result),
          );
          if (current.exists) return validSnapshot(current.value, scope);
          let previous: string | null;
          let legacy: boolean;
          try {
            const raw = options.legacyStorage.getItem(key);
            legacy = raw === null;
            previous = legacy
              ? options.legacyStorage.getItem(
                  legacyFormalWorkspaceSessionStorageKey(scope),
                )
              : raw;
          } catch (cause) {
            throw new Error(
              '无法读取旧工作台恢复记录，请恢复浏览器存储访问后重试。',
              { cause },
            );
          }
          if (previous === null) return null;
          const tabs = parseFormalWorkspaceSnapshot(previous, scope, legacy);
          if (!tabs)
            throw new Error(
              '旧工作台恢复数据与当前身份或权限不匹配，请保留现场后重试。',
            );
          const compact = serializeFormalWorkspaceTabs(tabs, scope);
          validSnapshot(compact, scope);
          // Another tab may have committed since the readonly miss or legacy read.
          return write(database, key, scope, compact, undefined, true);
        });
        expected.set(key, loaded);
        return loaded;
      });
    },
    save(scope, serialized, mergePrevious) {
      const key = formalWorkspaceSessionStorageKey(scope);
      return queued(key, async () => {
        if (!expected.has(key))
          throw new Error('请先读取当前工作台恢复记录再保存。');
        validSnapshot(serialized, scope);
        const committed = await withDatabase((database) =>
          write(
            database,
            key,
            scope,
            serialized,
            mergePrevious,
            false,
            expected.get(key),
          ),
        );
        expected.set(key, committed);
        return committed;
      });
    },
    takeOver(scope, serialized, conflictingSnapshot, mergePrevious) {
      const key = formalWorkspaceSessionStorageKey(scope);
      return queued(key, async () => {
        if (!expected.has(key))
          throw new Error('请先读取当前工作台恢复记录再保存。');
        validSnapshot(serialized, scope);
        if (conflictingSnapshot !== null)
          validSnapshot(conflictingSnapshot, scope);
        const committed = await withDatabase((database) =>
          write(
            database,
            key,
            scope,
            serialized,
            mergePrevious,
            false,
            expected.get(key),
            conflictingSnapshot,
          ),
        );
        expected.set(key, committed);
        return committed;
      });
    },
  };
}
